import React, { useState } from 'react';
import {
  KanbanSquare,
  GripVertical,
  Trash2,
  Edit2,
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSettings } from '../../context/SettingsContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const Pipelines = () => {
  // Protected system stages that cannot be deleted or renamed
  const PROTECTED_STAGES = [
    "New Lead",
    "In Progress",
    "Quoted",
    "Approved",
    "Booked",
    "Scheduled",
    "Completed",
    "Archived"
  ];

  const {
    pipelineStages,
    isSettingsLoading,
    addStage,
    renameStage,
    deleteStage,
    reorderStages,
  } = useSettings();

  const [newStageName, setNewStageName] = useState('');
  const [editingStage, setEditingStage] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Separate protected and custom stages
  const protectedStagesInOrder = PROTECTED_STAGES.filter(s => pipelineStages.includes(s));
  const customStages = pipelineStages.filter(s => !PROTECTED_STAGES.includes(s));
  
  // Combine: protected stages first (in canonical order), then custom stages
  const sortedStages = [...protectedStagesInOrder, ...customStages];

  // Check if a stage is protected
  const isProtectedStage = (stageName) => {
    return PROTECTED_STAGES.includes(stageName);
  };

  const handleAddStage = async () => {
    if (!newStageName.trim()) return;
    await addStage(newStageName);
    setNewStageName('');
  };

  const handleDeleteStage = async (stage) => {
    if (isProtectedStage(stage)) {
      toast.error("This stage is protected and cannot be deleted.");
      return;
    }
    await deleteStage(stage);
  };

  const handleStartEdit = (stage) => {
    if (isProtectedStage(stage)) {
      toast.error("This stage is protected and cannot be renamed.");
      return;
    }
    setEditingStage(stage);
    setEditingValue(stage);
  };

  const handleSaveEdit = async () => {
    if (!editingStage) return;
    if (!editingValue.trim() || editingValue === editingStage) {
      setEditingStage(null);
      setEditingValue('');
      return;
    }
    await renameStage(editingStage, editingValue);
    setEditingStage(null);
    setEditingValue('');
  };

  const handleStageReorder = async (result) => {
    const { destination, source } = result;

    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const reordered = Array.from(sortedStages);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    await reorderStages(reordered);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <KanbanSquare className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-secondary">Pipeline Stages</h2>
          <p className="text-sm text-muted-foreground">
            Customize your sales pipeline stages. System stages are protected and cannot be edited.
          </p>
        </div>
      </div>

      <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--cb-navy)' }}>
            Stage Configuration
          </h3>
          {isSettingsLoading && <span className="text-xs text-gray-400">Loading…</span>}
        </div>

        <p className="text-sm text-gray-500 mb-2">
          System stages (New Lead, In Progress, Quoted, Booked, Scheduled, Completed, Archived) are protected and cannot be edited or deleted.
        </p>

        {/* Stage list - protected stages first, then custom stages with drag-and-drop */}
        <div className="space-y-2">
          <DragDropContext onDragEnd={handleStageReorder}>
            <Droppable droppableId="settings-stages">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                  {sortedStages.map((stage, index) => {
                    const isProtected = isProtectedStage(stage);
                    return (
                      <Draggable 
                        key={stage} 
                        draggableId={stage} 
                        index={index}
                        isDragDisabled={isProtected}
                      >
                        {(draggableProvided, snapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            className={`flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 transition-all duration-150 ${
                              isProtected 
                                ? 'bg-gray-100 opacity-70' 
                                : 'bg-gray-50'
                            } ${
                              snapshot.isDragging ? 'shadow-lg scale-[1.01] bg-white' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {/* Drag handle */}
                              <div
                                {...draggableProvided.dragHandleProps}
                                onMouseDown={(e) => e.preventDefault()}
                                className={`p-1 rounded ${
                                  isProtected 
                                    ? 'cursor-not-allowed' 
                                    : 'hover:bg-gray-100 cursor-grab active:cursor-grabbing'
                                }`}
                              >
                                <GripVertical className={`h-4 w-4 ${
                                  isProtected ? 'text-gray-300' : 'text-gray-400'
                                }`} />
                              </div>
                              {editingStage === stage && !isProtected ? (
                                <Input
                                  autoFocus
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={handleSaveEdit}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveEdit();
                                    if (e.key === 'Escape') {
                                      setEditingStage(null);
                                      setEditingValue('');
                                    }
                                  }}
                                  className="h-8 text-sm max-w-xs"
                                />
                              ) : (
                                <span className={`text-sm ${
                                  isProtected ? 'font-medium text-gray-700' : 'text-gray-800'
                                }`}>
                                  {stage}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-1">
                              {isProtected ? (
                                <span className="text-xs text-gray-500">System stage · not editable</span>
                              ) : (
                                <>
                                  {/* Edit button for custom stages */}
                                  <button
                                    type="button"
                                    onClick={() => handleStartEdit(stage)}
                                    className="p-1 rounded hover:bg-gray-100"
                                    title="Rename stage"
                                  >
                                    <Edit2 className="h-4 w-4 text-gray-500" />
                                  </button>

                                  {/* Delete button for custom stages */}
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteStage(stage)}
                                    className="p-1 rounded hover:bg-red-50"
                                    title="Delete stage"
                                  >
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        {/* Add stage */}
        <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
          <Input
            placeholder="Add new stage…"
            value={newStageName}
            onChange={(e) => setNewStageName(e.target.value)}
            className="max-w-xs h-9 text-sm"
          />
          <Button
            type="button"
            size="sm"
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            onClick={handleAddStage}
          >
            Add Stage
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Pipelines;
